import { Typography } from '@mui/material';
import { t } from 'i18next';
import moment from 'moment';
import { useContext, useEffect, useState, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { Banner, BoxFlex, BrowserOnly, Countdown, Loading } from '@woovi/ui';

import type { PaymentLinkCompletedRedirect_charge$key } from './__generated__/PaymentLinkCompletedRedirect_charge.graphql.ts';
import PaymentContext from '../../context/PaymentContext.tsx';

type Props = {
  charge: PaymentLinkCompletedRedirect_charge$key;
};

const REDIRECT_TIMEOUT = 5000; // 5 seconds

const PaymentLinkCompletedRedirect = (props: Props): ReactNode => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const charge = useFragment(
    graphql`
      fragment PaymentLinkCompletedRedirect_charge on Charge {
        redirectUrl
        status
      }
    `,
    props.charge,
  );

  const { startRedirect } = useContext(PaymentContext);

  useEffect(() => {
    if (!charge?.redirectUrl || isRedirecting) {
      return;
    }

    if (startRedirect) {
      setIsRedirecting(true);
      setTimeout(() => {
        if (charge.redirectUrl) {
          window.open(charge.redirectUrl, '_self');
        }
      }, REDIRECT_TIMEOUT);
    }
  }, [startRedirect]);

  const now = moment().tz('America/Sao_Paulo');
  const redirectStart = now.add(REDIRECT_TIMEOUT, 'milliseconds');

  if (!startRedirect || !charge.redirectUrl) {
    return null;
  }

  return (
    <Banner variant='outline' color='primary'>
      <BoxFlex
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%',
        }}
      >
        <BrowserOnly>
          <Countdown
            targetDate={redirectStart.toISOString()}
            fallback={<Loading size={20} />}
          />
        </BrowserOnly>
        <Typography
          variant='subtitle1'
          fontWeight='bold'
          align='center'
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {t('You are being redirected to {{redirectUrl}}', {
            redirectUrl: charge.redirectUrl,
          })}
        </Typography>
      </BoxFlex>
    </Banner>
  );
};

export default PaymentLinkCompletedRedirect;
